import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const FLAGS = {
  'United Kingdom': '🇬🇧', Canada: '🇨🇦', Turkey: '🇹🇷', Poland: '🇵🇱', Australia: '🇦🇺', Germany: '🇩🇪',
};

const STAGE_LABELS = {
  LEAD: 'Lead', AGENT_ASSIGNED: 'Agent Assigned', DOCUMENT_COLLECTION: 'Document Review',
  INITIAL_FEE_PAID: 'Fee Paid', SUBMITTED: 'Submitted', OFFER_RECEIVED: 'Offer Received',
  DEPOSIT_PAID: 'Deposit Paid', OFFICIAL_ACCEPTANCE: 'Accepted', VISA_APPLICATION: 'Visa Processing',
  VISA_OUTCOME: 'Visa Outcome', TRAVEL_PREP: 'Travel Prep', ARRIVED: 'Arrived', CLOSED: 'Closed',
};

export default function AdminApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [meta, setMeta] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', country: '', stage: '', agentId: '', search: '' });
  const [selected, setSelected] = useState([]);

  const token = localStorage.getItem('adminToken');

  const fetchApplications = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    params.set('page', meta.page);

    fetch(`${API_BASE}/admin/applications?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setApplications(json.data);
          setMeta(json.meta);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchApplications, [filters, meta.page]);

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="pt-16 md:pt-2 md:ml-64 min-h-screen p-8">
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-3xl font-bold text-[#00255a]">Applications Management</h2>
            <p className="text-[#64748b]">Manage and track international student and work visa pipelines.</p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          <FilterSelect
            label="Type"
            value={filters.type}
            onChange={(v) => setFilters({ ...filters, type: v })}
            options={[['', 'All Types'], ['STUDY', 'Study'], ['JOB', 'Job']]}
          />
          <FilterSelect
            label="Country"
            value={filters.country}
            onChange={(v) => setFilters({ ...filters, country: v })}
            options={[['', 'All Countries'], ['United Kingdom', 'United Kingdom'], ['Canada', 'Canada'], ['Turkey', 'Turkey'], ['Poland', 'Poland']]}
          />
          <FilterSelect
            label="Stage"
            value={filters.stage}
            onChange={(v) => setFilters({ ...filters, stage: v })}
            options={[['', 'Any Stage'], ...Object.entries(STAGE_LABELS)]}
          />
          <div className="md:col-span-2 bg-white border border-[#e2e8f0] p-3 rounded-xl shadow-sm">
            <label className="block text-[10px] uppercase tracking-widest text-[#64748b] font-bold mb-1">Search</label>
            <input
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Name or email..."
              className="w-full bg-transparent border-none p-0 text-sm font-semibold focus:ring-0 text-[#00255a] outline-none"
            />
          </div>
          <button
            onClick={fetchApplications}
            className="text-white rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
          >
            <span className="material-symbols-outlined">filter_alt</span>
            Apply Filters
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-[0px_10px_30px_rgba(30,60,114,0.05)] border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#f2f4f6]/50 border-b border-[#e2e8f0]">
                  <th className="p-4 w-12 text-center">
                    <input type="checkbox" className="rounded border-[#c4c6d1] text-[#00255a]" />
                  </th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest">Applicant Name</th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest">Type</th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest">Country</th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest">Current Stage</th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest">Assigned Agent</th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest">Date Submitted</th>
                  <th className="p-4 text-[#64748b] uppercase text-[11px] tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {loading ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">Loading...</td></tr>
                ) : applications.length === 0 ? (
                  <tr><td colSpan="8" className="p-12 text-center text-[#64748b]">No applications match your filters.</td></tr>
                ) : (
                  applications.map((app) => (
                    <tr key={app.id} className={`hover:bg-[#f2f4f6] transition-colors group ${selected.includes(app.id) ? 'bg-[#00255a]/5' : ''}`}>
                      <td className="p-4 text-center">
                        <input
                          type="checkbox"
                          checked={selected.includes(app.id)}
                          onChange={() => toggleSelect(app.id)}
                          className="rounded border-[#c4c6d1] text-[#00255a]"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#8fb3ff] flex items-center justify-center text-white text-xs font-bold">
                            {app.applicant?.fullName?.charAt(0)}
                          </div>
                          <span className="font-bold text-[#0f172a]">{app.applicant?.fullName}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${
                          app.type === 'STUDY' ? 'bg-[#00255a]/10 text-[#00255a]' : 'bg-[#79161e]/10 text-[#862026]'
                        }`}>
                          {app.type}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {FLAGS[app.opportunity?.country || app.formResponses?.targetCountry] || '🌍'}
                          </span>
                          <div>
                            <span className="text-sm font-medium block">
                              {app.opportunity?.country || app.formResponses?.targetCountry || '—'}
                            </span>
                            {!app.opportunity && (app.formResponses?.fieldOfStudy || app.formResponses?.sector) && (
                              <span className="text-[11px] text-[#64748b]">
                                {app.formResponses.fieldOfStudy || app.formResponses.sector}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-3 py-1.5 rounded-full text-[11px] font-bold bg-[#8fb3ff]/20 text-[#174388] border border-[#8fb3ff]/30">
                          {STAGE_LABELS[app.currentStage] || app.currentStage}
                        </span>
                      </td>
                      <td className="p-4 text-sm font-medium text-[#64748b]">{app.assignedAgent?.fullName || 'Unassigned'}</td>
                      <td className="p-4 text-sm font-medium text-[#64748b]">{new Date(app.createdAt).toLocaleDateString()}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Link to={`/admin/applications/${app.id}`} className="p-2 hover:bg-[#e6e8ea] rounded-lg text-[#00255a] transition-all">
                            <span className="material-symbols-outlined text-[20px]">visibility</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 bg-[#f2f4f6]/30 border-t border-[#e2e8f0] flex items-center justify-between">
            <p className="text-sm text-[#64748b]">
              Showing <span className="font-bold text-[#0f172a]">{applications.length}</span> of{' '}
              <span className="font-bold text-[#0f172a]">{meta.total}</span> applications
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMeta({ ...meta, page: Math.max(1, meta.page - 1) })}
                disabled={meta.page === 1}
                className="p-2 rounded-lg border border-[#e2e8f0] hover:bg-[#f2f4f6] transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[18px]">chevron_left</span>
              </button>
              <span className="text-sm font-bold text-[#00255a] px-3">
                Page {meta.page} of {Math.max(1, Math.ceil(meta.total / meta.limit))}
              </span>
              <button
                onClick={() => setMeta({ ...meta, page: meta.page + 1 })}
                disabled={meta.page >= Math.ceil(meta.total / meta.limit)}
                className="p-2 rounded-lg border border-[#e2e8f0] hover:bg-[#f2f4f6] transition-colors disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-[18px]">chevron_right</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="bg-white border border-[#e2e8f0] p-3 rounded-xl shadow-sm">
      <label className="block text-[10px] uppercase tracking-widest text-[#64748b] font-bold mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-none p-0 text-sm font-semibold focus:ring-0 text-[#00255a] outline-none"
      >
        {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
      </select>
    </div>
  );
}